/* ============================================================
   GOB hero — set search + media slideshow
   Search: Shopify predictive search (/search/suggest.json). No app required.

   2026-08 — search measurement.
   Shopify's search reports only count sessions that load /search. A suggestion
   click goes straight to the product, so the better this hero works the less of
   it appears in that report. Two changes:

     1. A "See all results for X" row at the foot of the list. It goes to
        /search, so it is counted, and it is the right affordance anyway for a
        broad query like "star wars" where six suggestions are not enough.
     2. A gob_hero_search event published to Shopify's analytics bus, picked up
        by the custom pixel in Settings > Customer events. This is the only way
        to see the queries that never reach /search — above all the ones that
        return nothing, which are a list of sets we do not yet make a kit for.

   The event is NOT fired per keystroke. It waits for the query to settle, so
   typing 42083 reports once, not five times.

   2026-08-18 — no-results panel.
   15.3% of hero searches return nothing, and 72% of the visitors who hit that
   screen never clicked a product again. The old empty state offered a single
   link to 700+ kits. It now offers the two routes that actually exist. See
   empty() below.
   ============================================================ */
(function () {
  'use strict';

  var DEBOUNCE = 250, FETCH = 10, DISPLAY = 6;
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  /* lowercase, and flatten every separator to a single space, so that
     "Notre-Dame", "Mercedes-AMG" and "X-Files" compare cleanly against
     what people actually type. */
  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }
  /* Wall-mounts and nameplates are not light kits. product_type is blank on
     the wall-mounts, so the old `p.type || 'Light kit'` fallback mislabelled
     every one of them. Read the title, which is always right. */
  function kindOf(p) {
    var t = String((p && p.title) || '').toLowerCase();
    if (t.indexOf('wall-mount') > -1 || t.indexOf('wall mount') > -1) {
      return { label: 'Display wall-mount', cta: 'View mount' };
    }
    if (t.indexOf('nameplate') > -1) {
      return { label: 'LED nameplate', cta: 'View nameplate' };
    }
    return { label: (p && p.type) || 'Light kit', cta: 'View kit' };
  }

  /* ---------------------------------------------------------- slideshow */
  function initSlider(el) {
    var slides = Array.prototype.slice.call(el.querySelectorAll('[data-gob-slide]'));
    if (!slides.length) return;

    var caps  = Array.prototype.slice.call(el.querySelectorAll('[data-gob-cap]'));
    var dots  = Array.prototype.slice.call(el.querySelectorAll('[data-gob-dot]'));
    var interval = parseInt(el.dataset.interval, 10) || 6000;
    var autoplay = el.dataset.autoplay === 'true' && slides.length > 1 && !REDUCED;
    var i = 0, timer = null, paused = false;

    function playVideo(slide) {
      var v = slide.querySelector('[data-gob-video]');
      if (!v || REDUCED) return;
      v.addEventListener('canplay', function () { v.classList.add('is-ready'); }, { once: true });
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    function stopVideo(slide) {
      var v = slide.querySelector('[data-gob-video]');
      if (v) v.pause();
    }

    function goTo(n, viaUser) {
      if (n === i) return;
      var prev = i;
      i = (n + slides.length) % slides.length;

      slides[prev].classList.remove('is-active');
      slides[prev].classList.add('is-leaving');
      setTimeout(function () { slides[prev].classList.remove('is-leaving'); }, 800);
      stopVideo(slides[prev]);

      slides[i].classList.add('is-active');
      playVideo(slides[i]);

      caps.forEach(function (c, n2) { c.classList.toggle('is-active', n2 === i); });

      dots.forEach(function (d, n2) {
        d.classList.remove('is-active', 'is-paused');
        d.classList.toggle('is-seen', n2 < i);
        d.setAttribute('aria-current', n2 === i ? 'true' : 'false');
      });
      if (dots[i]) {
        dots[i].style.setProperty('--gobh-dur', interval + 'ms');
        void dots[i].offsetWidth;                 // restart the fill animation
        dots[i].classList.add('is-active');
      }

      if (viaUser) restart();
    }

    function next() { goTo(i + 1); }
    function restart() { clearInterval(timer); if (autoplay && !paused) timer = setInterval(next, interval); }
    function pause() {
      paused = true; clearInterval(timer);
      if (dots[i]) dots[i].classList.add('is-paused');
    }
    function resume() {
      paused = false;
      if (dots[i]) dots[i].classList.remove('is-paused');
      restart();
    }

    dots.forEach(function (d) {
      d.addEventListener('click', function () { goTo(parseInt(d.dataset.i, 10), true); });
    });

    el.addEventListener('pointerenter', pause);
    el.addEventListener('pointerleave', resume);
    el.addEventListener('focusin', pause);
    el.addEventListener('focusout', resume);
    document.addEventListener('visibilitychange', function () {
      document.hidden ? pause() : resume();
    });

    /* swipe */
    var x0 = null;
    el.addEventListener('touchstart', function (e) { x0 = e.changedTouches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 45) goTo(i + (dx < 0 ? 1 : -1), true);
      x0 = null;
    }, { passive: true });

    /* start once the section is near the viewport */
    function start() {
      playVideo(slides[0]);
      if (dots[0]) {
        dots[0].style.setProperty('--gobh-dur', interval + 'ms');
        dots[0].classList.add('is-active');
      }
      restart();
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { start(); io.unobserve(en.target); } });
      }, { rootMargin: '150px' });
      io.observe(el);
    } else { start(); }
  }

  /* ---------------------------------------------------------- search */
  function initSearch(hero) {
    var form = hero.querySelector('[data-gob-search]');
    if (!form) return;

    var input = form.querySelector('input[name="q"]');
    var pop   = form.querySelector('.gobh__pop');
    var list  = form.querySelector('[data-gob-list]');
    var go    = form.querySelector('[data-gob-go]');
    var fallbackHref = (hero.querySelector('.gobh__fallback') || {}).href || (root() + 'collections/all');

    var timer = null, ctrl = null, active = -1, opts = [], results = [];

    /* the search URL the form itself posts to, so locale prefixes are kept */
    var searchAction = form.getAttribute('action') || (root() + 'search');
    function searchUrl(q) {
      return searchAction + '?q=' + encodeURIComponent(q) +
             '&type=product&options[prefix]=last';
    }

    /* ---- measurement -------------------------------------------------- */
    var settleTimer = null;      /* fires once typing stops */
    var lastQuery = '';          /* what the visible list is showing */
    var lastCount = -1;
    var sent = {};               /* query+action already reported this pageview */

    function track(action, extra) {
      if (!lastQuery || lastCount < 0) return;
      var key = action + '|' + lastQuery.toLowerCase();
      if (sent[key]) return;
      sent[key] = 1;

      var payload = {
        query: lastQuery,
        results: lastCount,
        action: action,                       /* settled | suggestion | see_all | submit
                                                 | zero_diy | zero_kits | zero_product */
        source: 'homepage_hero'
      };
      if (extra) for (var k in extra) payload[k] = extra[k];

      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
          Shopify.analytics.publish('gob_hero_search', payload);
        }
      } catch (e) { /* measurement must never break the search */ }
    }

    function settle() {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () { track('settled'); }, 1500);
    }

    function open()  { pop.hidden = false; input.setAttribute('aria-expanded', 'true'); }
    function close() {
      pop.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1; opts = [];
    }
    function sync() { if (go) go.disabled = !input.value.trim(); }

    function loading() {
      list.innerHTML = '<div class="gobh__state"><span class="gobh__spin" aria-hidden="true"></span>' +
                       '<span role="status">Searching kits…</span></div>';
    }

    /* ---- no-results panel ---------------------------------------------
       A dead end used to offer one link to 700+ products, and 72% of the
       visitors who reached it never clicked anything again. It now states
       the plain truth and gives the two routes that actually exist: buy
       the components and light it yourself, or pick a set we do cover.

       Every string, both links and the featured product come from section
       settings, so the price follows the visitor's market and nothing is
       hard-coded to one currency. An option with no link is skipped, so a
       half-configured section degrades quietly rather than rendering a
       row that goes nowhere.                                             */
    function zAttr(name, dflt) {
      var v = hero.getAttribute('data-z-' + name);
      return (v === null || v === '') ? (dflt || '') : v;
    }
    function zRow(mod, title, sub, cta, href, act) {
      if (!href || !title) return '';
      return '<a class="gobh__zrow' + mod + '" href="' + esc(href) + '" data-z-act="' + act + '">' +
             '<span class="gobh__zt"><b>' + esc(title) + '</b>' +
             (sub ? '<span>' + esc(sub) + '</span>' : '') + '</span>' +
             '<span class="gobh__zgo">' + esc(cta) + ' &rarr;</span></a>';
    }
    function empty(q) {
      /* the template is escaped first, then the token is swapped for the
         escaped query — braces survive escaping, so the order is safe */
      var head = esc(zAttr('head', 'We don’t make a kit for “{query}”.'))
                   .replace('{query}', esc(q));

      var rows =
        zRow(' gobh__zrow--pri',
             zAttr('a-title', 'Light it yourself'),
             zAttr('a-sub',   'Components that work with any build'),
             zAttr('a-cta',   'Shop parts'),
             zAttr('a-href',  root() + 'collections/lego-light-kits-accessories'),
             'zero_diy') +
        zRow('',
             zAttr('b-title', 'Pick a set we do light'),
             zAttr('b-sub',   'Ready-made kits, fitted to the set'),
             zAttr('b-cta',   'Browse kits'),
             zAttr('b-href',  fallbackHref),
             'zero_kits');

      var pHref = zAttr('p-href'), prod = '';
      if (pHref) {
        var pImg = zAttr('p-img');
        prod = '<a class="gobh__zprod" href="' + esc(pHref) + '" data-z-act="zero_product">' +
               (pImg ? '<img src="' + esc(pImg) + '" alt="" width="44" height="44" loading="lazy">' : '') +
               '<span class="gobh__zt"><b>' + esc(zAttr('p-title')) + '</b>' +
               '<span>' + esc(zAttr('p-sub')) + '</span></span>' +
               '<span class="gobh__zgo">' + esc(zAttr('p-cta', 'View')) + ' &rarr;</span></a>';
      }

      list.innerHTML =
        '<div class="gobh__zhead"><b>' + head + '</b>' +
        '<span>' + esc(zAttr('sub', 'Two things you can do about it.')) + '</span></div>' +
        '<div class="gobh__zrows">' + rows + '</div>' + prod;
    }

    function seeAllRow(q) {
      return '<a class="gobh__all" role="option" aria-selected="false" id="GobHeroAll" ' +
             'href="' + esc(searchUrl(q)) + '" data-gob-all>' +
             '<span class="t">See all results for <b>' + esc(q) + '</b></span>' +
             '<span class="go">&rarr;</span></a>';
    }
    function render(products, q) {
      results = products;
      lastQuery = q;
      lastCount = products.length;
      settle();
      if (!products.length) { opts = []; return empty(q); }
      list.innerHTML = products.map(function (p, n) {
        var src = (p.featured_image && p.featured_image.url) || p.image || '';
        var img = src ? '<img src="' + esc(src) + '" alt="" width="44" height="44" loading="lazy">' : '';
        var kind = kindOf(p);
        return '<a class="gobh__opt" role="option" aria-selected="false" id="GobHeroOpt' + n +
               '" data-i="' + n + '" href="' + esc(p.url) + '">' + img +
               '<span class="t"><b>' + esc(p.title) + '</b>' +
               '<span>' + esc(kind.label) + '</span></span>' +
               '<span class="go">' + esc(kind.cta) + ' &rarr;</span></a>';
      }).join('') + seeAllRow(q);
      opts = Array.prototype.slice.call(list.querySelectorAll('.gobh__opt, .gobh__all'));
      active = -1;
    }
    function highlight() {
      opts.forEach(function (o, n) {
        var on = n === active;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) { input.setAttribute('aria-activedescendant', o.id); o.scrollIntoView({ block: 'nearest' }); }
      });
    }
    /* ---- relevance re-rank --------------------------------------------
       Shopify returns suggestions in its own order and offers no way to
       weight one field above another. For a set-number query like 42083
       that means products for OTHER sets can outrank the real ones — the
       search index holds residue from products duplicated from one
       another, and no editable field on those products contains the
       number, so it cannot be fixed in the data. Reorder here instead:
       anything whose TITLE contains what was typed outranks anything that
       only matched on a tag or SKU. Shopify's own order is preserved
       inside each tier, so this can only promote an obviously-better
       match — it never invents one.                                      */
    function rank(products, q) {
      var needle = norm(q);
      if (!needle) return products;
      var words = needle.split(' ').filter(Boolean);
      var tight = needle.replace(/ /g, '');            /* "starwars", "xfiles", "deathstar" */
      function score(p) {
        var t = norm(p.title), s = 0;
        /* the space-insensitive compare is what makes a closed-up query
           like "gameboy" or "deathstar" recognise "Game Boy" / "Death Star" */
        if (t.indexOf(needle) > -1 || t.replace(/ /g, '').indexOf(tight) > -1) s = 2;
        else if (words.length > 1 && words.every(function (w) { return t.indexOf(w) > -1; })) s = 1;
        /* among equally-good title matches, the light kit is the product
           people came for — nameplates and wall-mounts are the add-on. */
        if (s && t.indexOf('light kit') > -1) s += 0.5;
        return s;
      }
      return products
        .map(function (p, i) { return { p: p, s: score(p), i: i }; })
        .sort(function (a, b) { return (b.s - a.s) || (a.i - b.i); })
        .map(function (x) { return x.p; });
    }

    function query(q) {
      if (ctrl) ctrl.abort();
      ctrl = ('AbortController' in window) ? new AbortController() : null;
      var url = root() + 'search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=product&resources[limit]=' + FETCH +
                '&resources[options][unavailable_products]=last' +
                '&resources[options][fields]=title,product_type,variants.title,variants.sku,tag';
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var found = ((d.resources || {}).results || {}).products || [];
          render(rank(found, q).slice(0, DISPLAY), q);
        })
        .catch(function (err) { if (!err || err.name !== 'AbortError') close(); });
    }
    function run() {
      var q = input.value.trim();
      sync();
      clearTimeout(settleTimer);        /* still typing — nothing has settled */
      if (q.length < 2) { close(); return; }
      open(); loading();
      clearTimeout(timer);
      timer = setTimeout(function () { query(q); }, DEBOUNCE);
    }

    input.addEventListener('input', run);
    input.addEventListener('focus', function () { if (input.value.trim().length >= 2) run(); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (!opts.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); active = (active + 1) % opts.length; highlight(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = (active - 1 + opts.length) % opts.length; highlight(); }
      else if (e.key === 'Enter' && active > -1) {
        e.preventDefault();
        opts[active].click();           /* click, so the reporting below runs */
      }
    });

    /* one delegated handler covers a suggestion, the see-all row and the
       three no-results routes */
    list.addEventListener('click', function (e) {
      /* no-results rows report as zero_diy / zero_kits / zero_product, so the
         value of that panel is measured rather than assumed */
      var z = e.target.closest ? e.target.closest('[data-z-act]') : null;
      if (z) { track(z.getAttribute('data-z-act')); return; }
      var all = e.target.closest ? e.target.closest('.gobh__all') : null;
      if (all) { track('see_all'); return; }
      var opt = e.target.closest ? e.target.closest('.gobh__opt') : null;
      if (!opt) return;
      var i = parseInt(opt.getAttribute('data-i'), 10);
      var p = results[i] || {};
      track('suggestion', {
        position: i + 1,
        product_title: p.title || '',
        product_url: p.url || ''
      });
    });

    form.addEventListener('submit', function (e) {
      if (!input.value.trim()) { e.preventDefault(); input.focus(); return; }
      clearTimeout(settleTimer);
      track('submit');                  /* this one also lands in /search natively */
    });
    document.addEventListener('click', function (e) { if (!form.contains(e.target)) close(); });

    sync();
  }

  /* ---------------------------------------------------------- boot */
  function initHero(hero) {
    if (hero.dataset.gobhInit === '1') return;
    hero.dataset.gobhInit = '1';
    var slider = hero.querySelector('[data-gob-slider]');
    if (slider) initSlider(slider);
    initSearch(hero);
  }
  function boot() { document.querySelectorAll('[data-gob-hero]').forEach(initHero); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
})();